// src/components/AppHeader.js
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
  useTheme,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LogoutIcon from '@mui/icons-material/Logout';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import StorageIcon from '@mui/icons-material/Storage';
import HelpIcon from '@mui/icons-material/Help';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useNavigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { useEntityContext } from './EntityContextSwitch';
import EntityContextSwitch from './EntityContextSwitch';
import AudioRecorderButton from './AudioRecorder/AudioRecorderButton';
import { useAuthStatus } from '../hooks/useAuthStatus';
import useMediaQuery from '@mui/material/useMediaQuery';

// eslint-disable-next-line max-lines-per-function
function AppHeader() {
  const { user, logout, isPlatformAdmin } = useAuth();
  const { currentEntity } = useEntityContext();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { authStatus, error: authError, checkAuthStatus } = useAuthStatus();

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    const fetchAlertCount = async () => {
      try {
        // Always show 4 alerts to match Dashboard demo mode
        // This keeps the avatar badge consistent with the dashboard panel
        setAlertCount(4);
      } catch (_error) {
        setAlertCount(4);
      }
    };

    if (user) {
      fetchAlertCount();
    }
  }, [user]);

  // Show auth error in snackbar only when auth check has been performed
  useEffect(() => {
    if (authStatus === false && authError) {
      setSnackbarMessage(authError || 'Authentication failed. Please log in again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [authError, authStatus]);

  const testAuth = async () => {
    try {
      // Force refresh the auth status
      const isAuthenticated = await checkAuthStatus(true);
      setSnackbarMessage(isAuthenticated ? 'Authentication successful' : 'Authentication failed');
      setSnackbarSeverity(isAuthenticated ? 'success' : 'error');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(error.message || 'Auth test failed');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleMenuItemClick = (route) => {
    setMenuOpen(false);
    navigate(route);
  };

  return (
    <>
      <AppBar position="fixed" sx={{
        zIndex: theme.zIndex.drawer + 1,
        width: '100%',
        left: 0,
        right: 0
      }}>
        <Toolbar sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto'
        }}>
          {/* Faana Menu Button - Compact logo on mobile, hamburger on desktop */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => setMenuOpen(!menuOpen)}
            sx={{
              mr: isMobile ? 1 : 2,
              p: isMobile ? 0.5 : 1,
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Mobile: Show entity selector directly in header */}
          {isMobile ? (
            <Box sx={{ flexGrow: 1, maxWidth: 180 }}>
              <EntityContextSwitch compact />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                component="img"
                src="./faana_logo.png"
                alt="Faana Logo"
                sx={{
                  height: { xs: '30px', sm: '40px' },
                  width: 'auto',
                  mr: 2,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: '#C9A0DC',
                  fontFamily: 'Caveat, cursive',
                  fontWeight: 700,
                  fontSize: { xs: '0.9rem', sm: '1.2rem' },
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                Intelligence Hub
              </Typography>
            </Box>
          )}

          {/* Spacer for layout */}
          <Box sx={{ flexGrow: 1 }} />

          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {authStatus !== null && (
                <Tooltip title={authStatus ? "Authenticated" : "Authentication Failed. You need to login again"}>
                  {authStatus ? (
                    <CheckCircleIcon sx={{ color: 'green', mr: 1, display: { xs: 'none', sm: 'block' } }} />
                  ) : (
                    <CancelIcon sx={{ color: 'red', mr: 1, display: { xs: 'none', sm: 'block' } }} />
                  )}
                </Tooltip>
              )}

              {/* Audio Recorder Button */}
              <Box sx={{ mr: 1 }}>
                <AudioRecorderButton
                  entityId={currentEntity?.id}
                  entityName={currentEntity?.name}
                  showInHeader={true}
                  onSave={(data) => {
                    console.log('Audio note saved:', data);
                  }}
                />
              </Box>

              <Typography
                variant="body2"
                sx={{
                  mr: 2,
                  display: { xs: 'none', md: 'block' }
                }}
              >
                {user.email}
              </Typography>
              <IconButton
                size="large"
                onClick={handleMenu}
                color="inherit"
                aria-label={`User menu for ${user.displayName || user.email}${alertCount > 0 ? `. ${alertCount} new alerts` : ''}`}
                aria-haspopup="true"
                aria-expanded={Boolean(anchorEl)}
                aria-controls={anchorEl ? 'menu-appbar' : undefined}
              >
                <Badge
                  badgeContent={alertCount}
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      right: 3,
                      top: 3,
                      fontSize: '0.7rem',
                      minWidth: '16px',
                      height: '16px'
                    }
                  }}
                >
                  <Avatar
                    alt={user.displayName || 'User'}
                    src={user.photoURL || ''}
                    sx={{ width: 32, height: 32 }}
                  />
                </Badge>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={handleClose}>
                  <Box>
                    <Typography>{user.displayName || user.email}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => {
                  handleClose();
                  navigate('/settings');
                }}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
                <MenuItem onClick={() => {
                  handleClose();
                  testAuth();
                }}>
                  <ListItemIcon>
                    <VerifiedUserIcon fontSize="small" />
                  </ListItemIcon>
                  Test Auth
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Hamburger Menu Drawer */}
      <Drawer
        anchor="left"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        sx={{
          zIndex: 1300,
          '& .MuiDrawer-paper': {
            zIndex: 1300,
          },
        }}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
        >
          {/* Entity Selector at top of menu */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <EntityContextSwitch />
          </Box>

          <List>
            <ListItem button onClick={() => handleMenuItemClick('/faana-agents')}>
              <ListItemIcon>
                <PsychologyIcon />
              </ListItemIcon>
              <ListItemText primary="Faana Agents" />
            </ListItem>

            <ListItem button onClick={() => handleMenuItemClick('/behavioral-workbench')}>
              <ListItemIcon>
                <PsychologyAltIcon />
              </ListItemIcon>
              <ListItemText primary="Behavioral Workbench" />
            </ListItem>

            {isPlatformAdmin && (
              <ListItem button onClick={() => handleMenuItemClick('/data-management')}>
                <ListItemIcon>
                  <StorageIcon />
                </ListItemIcon>
                <ListItemText primary="Data Sources" />
              </ListItem>
            )}

            {isPlatformAdmin && (
              <ListItem button onClick={() => handleMenuItemClick('/admin')}>
                <ListItemIcon>
                  <AdminPanelSettingsIcon />
                </ListItemIcon>
                <ListItemText primary="Admin" />
              </ListItem>
            )}

            <ListItem button onClick={() => handleMenuItemClick('/help')}>
              <ListItemIcon>
                <HelpIcon />
              </ListItemIcon>
              <ListItemText primary="Help & Support" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
}

export default AppHeader;